// The companion speed dial of an open app: ONE component for cortex's Apps page (apps/web wires it to its
// api and pages) and a project's standalone app (this shell), a one-to-one port of the old cortex dial
// (AssistantChat.jsx `speedDial`): one 44px round #3c5d88 button at bottom-6/right-6, 55% opaque until
// hovered or open, a plus that turns into an X; the companions fan out ABOVE it as coloured pills, chat
// nearest the button. WHICH companions is the app's declaration (GET …/apps/:app/companions, the
// structure routes), as the host reads it. A page companion opens over the app in a large window, so the
// app stays where it was; the chat and the guides open their own overlays. The dial holds only the
// companions; there is no bar. An app whose declaration says `spec.dial: "app"` places the entry itself (its
// frame's header button calls `openCompanions()` of @cortex/app-sdk/client): then no round button floats over
// the app, and the pills open under the app's own entry.
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@cortex/ui';
import {
  BookOpen,
  Brain,
  Cpu,
  ArrowLeft,
  ExternalLink,
  GitBranch,
  GraduationCap,
  MessageSquare,
  Plus,
  ShieldCheck,
  Sparkles,
  UserCog,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router';
import { OPEN_CHAT_EVENT, START_TUTORIAL_EVENT, useCompanionsChanged } from './companions.tsx';
import { WindowRouter, type WindowControls, type WindowPlace } from './WindowRouter.tsx';

export type Companion = { key: string; label: string; page: string | null };

/** Where the launcher is: the host's floating button, or the app's own entry (its declaration's `spec.dial`). */
export type DialPlacement = 'host' | 'app';

/** What the dial reads from its host: the companions, their pages and where a page opens full. */
export interface DialHost {
  /** The companions, and where the app wants their launcher (a bare list: the host's floating button). */
  companions(
    slug: string,
    app: string,
  ): Promise<Companion[] | { companions: Companion[]; dial?: DialPlacement }>;
  pages: Record<string, () => Promise<React.ComponentType>>;
  pagePath(page: string, slug: string): string;
  /**
   * The window navigates on its own (an app's own page): a link inside a companion's page opens in the window, with
   * its back, the app staying underneath; "Open the full page" opens the window's current page full. Without it (cortex,
   * whose pages read the preview's own route) the window shows the one page and a link inside it leaves it.
   */
  windowNavigation?: boolean;
}

/** Per companion: its icon and colour — the old in-cortex dial's (AssistantChat.jsx DIAL_ITEMS). */
const CHROME: Record<string, { icon: LucideIcon; bg: string }> = {
  chat: { icon: MessageSquare, bg: '#3c5d88' },
  workflows: { icon: Workflow, bg: '#0f172a' },
  guides: { icon: GraduationCap, bg: '#7c3aed' },
  models: { icon: Brain, bg: '#0e7490' },
  pipeline: { icon: GitBranch, bg: '#4f46e5' },
  governance: { icon: ShieldCheck, bg: '#059669' },
  docs: { icon: BookOpen, bg: '#b45309' },
  // The app's own AI page (its admins only: the standalone shell adds it).
  ai: { icon: Cpu, bg: '#475569' },
  // The app's own Access page (its managers only: the standalone shell adds it, ADR 0066).
  access: { icon: UserCog, bg: '#be185d' },
};

/** A companion this dial has no chrome for yet: still drawn, with a neutral icon and colour, never dropped. */
const DEFAULT_CHROME = { icon: Sparkles, bg: '#334155' };

/** The icon and colour of a companion's pill: its own, or the neutral default for a key the dial does not know. */
export function dialChrome(key: string): { icon: LucideIcon; bg: string } {
  return CHROME[key] ?? DEFAULT_CHROME;
}

const DIAL_SHADOW = '0 4px 14px rgba(0,0,0,.28)';
/**
 * A pill's edge: the old shadow plus a 1px light ring. The old dial had only the shadow (AssistantChat.jsx
 * `boxShadow: "0 4px 14px rgba(0,0,0,.28)"`), and a dark shadow does not show on a dark app, so the
 * Workflows pill (#0f172a) disappeared against dark themes. The ring is white at 28% opacity, which is
 * invisible on a light app, so light themes look exactly like the old dial. On a dark app it outlines
 * every pill.
 */
const PILL_SHADOW = `0 0 0 1px rgba(255,255,255,.28), ${DIAL_SHADOW}`;

/** The app's entry, on this page (its frame's offset added): the pills open under it. */
type Anchor = { top: number; left: number; bottom: number; right: number };

/**
 * The box of the app's entry on this page: the box the app sent (in its frame's viewport) moved by the frame's own
 * place; null when the message did not come from a frame of this page.
 */
export function anchorOnPage(sent: unknown, frame: { top: number; left: number } | null): Anchor | null {
  if (!frame) return null;
  const a = sent as Partial<Anchor> | null | undefined;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const top = n(a?.top);
  const left = n(a?.left);
  const bottom = n(a?.bottom);
  const right = n(a?.right);
  if (top === null || left === null || bottom === null || right === null)
    // No box: under the frame's top edge, at the right of the page.
    return { top: frame.top, left: frame.left, bottom: frame.top + 48, right: Number.POSITIVE_INFINITY };
  return {
    top: top + frame.top,
    left: left + frame.left,
    bottom: bottom + frame.top,
    right: right + frame.left,
  };
}

/** The frame (an iframe of this page) a message came from, and its place on the page. */
function frameOf(source: MessageEventSource | null): { top: number; left: number } | null {
  if (!source) return null;
  for (const f of Array.from(document.querySelectorAll('iframe')))
    if (f.contentWindow === source) {
      const r = f.getBoundingClientRect();
      return { top: r.top, left: r.left };
    }
  return null;
}

const lazyPages = new Map<string, React.LazyExoticComponent<React.ComponentType>>();
function pageComponent(pages: Record<string, () => Promise<React.ComponentType>>, page: string) {
  const load = pages[page];
  if (!load) return null;
  let c = lazyPages.get(page);
  if (!c) lazyPages.set(page, (c = React.lazy(() => load().then((Page) => ({ default: Page })))));
  return c;
}

function CompanionWindow({
  host,
  companion,
  slug,
  onClose,
}: {
  host: DialHost;
  companion: Companion & { page: string };
  slug: string;
  onClose: () => void;
}) {
  const Page = pageComponent(host.pages, companion.page);
  const start = host.pagePath(companion.page, slug);
  const [place, setPlace] = React.useState<WindowPlace>({ path: start, depth: 0 });
  const controls = React.useRef<WindowControls | null>(null);
  const navigate = useNavigate();
  const leave = React.useCallback(
    (path: string) => {
      onClose();
      void navigate(path);
    },
    [onClose, navigate],
  );
  const page = React.useCallback((key: string) => pageComponent(host.pages, key), [host.pages]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="companion-window"
        data-companion={companion.key}
        data-window-path={host.windowNavigation ? place.path : undefined}
        className="bg-page-bg border-0 rounded-[10px] p-0 gap-0 shadow-2xl text-page-text sm:max-w-[min(1200px,94vw)] w-[94vw] h-[88vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="flex flex-row items-center gap-2 px-4 h-12 shrink-0 border-b border-page-divider bg-card-bg text-left space-y-0">
          {host.windowNavigation && place.depth > 0 && (
            <button
              type="button"
              onClick={() => controls.current?.back()}
              data-testid="companion-back"
              aria-label="Back"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border-0 bg-transparent text-page-text hover:bg-page-bg cursor-pointer"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <DialogTitle className="text-base font-semibold flex-1">{companion.label}</DialogTitle>
          <DialogDescription className="sr-only">
            The {companion.label} page of this project
          </DialogDescription>
          <Link
            to={host.windowNavigation ? place.path : start}
            onClick={onClose}
            data-testid="companion-open-page"
            className="inline-flex items-center gap-1 text-xs text-nav-active no-underline hover:underline mr-8"
          >
            <ExternalLink size={13} /> Open the full page
          </Link>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto" data-testid="companion-body">
          {Page ? (
            <React.Suspense fallback={<div className="p-6 text-sm text-page-muted">Loading…</div>}>
              {host.windowNavigation ? (
                <WindowRouter
                  start={start}
                  pages={host.pages}
                  page={page}
                  onPlace={setPlace}
                  controls={controls}
                  onLeave={leave}
                />
              ) : (
                <Page />
              )}
            </React.Suspense>
          ) : (
            <div className="p-6 text-sm text-page-muted">This page is not available.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** One companion's pill in the open dial. Every companion gets one (an unknown key the neutral chrome). */
export function DialPill({ companion: c, onPick }: { companion: Companion; onPick: (c: Companion) => void }) {
  const ch = dialChrome(c.key);
  const Icon = ch.icon;
  return (
    <button
      type="button"
      data-testid={`dial-${c.key}`}
      onClick={() => onPick(c)}
      style={{ background: ch.bg, boxShadow: PILL_SHADOW }}
      className="flex h-10 cursor-pointer items-center gap-2 rounded-full border-0 pl-3 pr-4 text-sm font-medium text-white transition-all hover:opacity-90"
    >
      <Icon className="h-5 w-5" /> {c.label}
    </button>
  );
}

export function CompanionDial({
  host,
  slug,
  app,
  hidden = false,
}: {
  host: DialHost;
  slug: string;
  app: string;
  /** The chat window is open: the dial steps aside (the old launcher did the same). */
  hidden?: boolean;
}) {
  const [companions, setCompanions] = React.useState<Companion[] | null>(null);
  const [placement, setPlacement] = React.useState<DialPlacement>('host');
  // Where the app's own entry is (placement 'app'), while its pills are open.
  const [anchor, setAnchor] = React.useState<Anchor | null>(null);
  const [open, setOpen] = React.useState(false);
  const [showing, setShowing] = React.useState<(Companion & { page: string }) | null>(null);
  // Bumped when the app's viewer options are saved: the dial reads its companions again at once.
  const [rev, setRev] = React.useState(0);
  useCompanionsChanged(slug, app, () => setRev((n) => n + 1));

  React.useEffect(() => {
    let alive = true;
    setCompanions(null);
    setOpen(false);
    host
      .companions(slug, app)
      .then((c) => {
        if (!alive) return;
        setCompanions(Array.isArray(c) ? c : c.companions);
        setPlacement(!Array.isArray(c) && c.dial === 'app' ? 'app' : 'host');
      })
      .catch(() => alive && setCompanions([]));
    return () => {
      alive = false;
    };
    // The host's reader is stable (a module constant or a memo); the app (or a save of its options) is what changes.
  }, [slug, app, rev]);

  React.useEffect(() => {
    if (hidden) setOpen(false);
  }, [hidden]);

  // The app's own entry (placement 'app'): its frame asks to open (or close) the companions under it.
  React.useEffect(() => {
    if (placement !== 'app') return;
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { source?: string; type?: string; anchor?: unknown } | null;
      if (d?.source !== 'cortex-app' || d.type !== 'companions') return;
      const at = anchorOnPage(d.anchor, frameOf(e.source));
      if (!at) return;
      setAnchor(at);
      setOpen((v) => !v);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [placement]);
  // The app's entry shows only when there is something to open: the host tells the app's frames how many companions
  // it offers, now and whenever a frame asks (`companions-hello`, a frame that loaded later).
  React.useEffect(() => {
    if (placement !== 'app' || companions === null) return;
    const offer = { source: 'cortex-host', type: 'companions', count: hidden ? 0 : companions.length };
    const tell = (w: MessageEventSource | Window | null | undefined) => {
      try {
        (w as Window | null)?.postMessage(offer, '*');
      } catch {
        /* the frame went away */
      }
    };
    for (const f of Array.from(document.querySelectorAll('iframe'))) tell(f.contentWindow);
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { source?: string; type?: string } | null;
      if (d?.source === 'cortex-app' && d.type === 'companions-hello' && frameOf(e.source)) tell(e.source);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [placement, companions, hidden]);
  // Open under the app's entry: Escape or a click elsewhere closes it (a click in the app's frame blurs this page).
  React.useEffect(() => {
    if (placement !== 'app' || !open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element | null)?.closest?.('[data-testid="companion-dial"]')) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('blur', close);
    };
  }, [placement, open]);

  const pick = (c: Companion) => {
    setOpen(false);
    if (c.key === 'chat') window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: {} }));
    else if (c.key === 'guides') window.dispatchEvent(new CustomEvent(START_TUTORIAL_EVENT));
    else if (c.page) setShowing({ ...c, page: c.page });
  };

  return (
    <>
      {/* Nothing to offer (every companion off, or the app draws its own chat), or not known yet (the read
          may take seconds on a slow network, and offering a Chat the app draws itself is wrong): no dial. */}
      {/* The app places the entry: no floating button; the pills open under the app's entry, nothing over the app
          until asked. */}
      {!hidden && placement === 'app' && companions !== null && companions.length > 0 && (
        <span
          hidden
          data-testid="companion-dial-app"
          data-companions={companions.map((c) => c.key).join(',')}
        />
      )}
      {!hidden && placement === 'app' && open && anchor && companions !== null && companions.length > 0 && (
        <div
          className="fixed z-[9999] flex flex-col items-end gap-2"
          style={{ top: anchor.bottom + 8, right: Math.max(8, window.innerWidth - anchor.right) }}
          data-testid="companion-dial"
          data-placement="app"
          data-companions={companions.map((c) => c.key).join(',')}
        >
          {companions.map((c) => (
            <DialPill key={c.key} companion={c} onPick={pick} />
          ))}
        </div>
      )}
      {!hidden && placement === 'host' && companions !== null && companions.length > 0 && (
        <div
          className="fixed bottom-6 right-6 z-[9999] hidden md:flex flex-col-reverse items-end gap-2"
          data-testid="companion-dial"
          data-companions={(companions ?? []).map((c) => c.key).join(',')}
        >
          <button
            type="button"
            data-testid="app-speed-dial"
            data-tour="app-speed-dial"
            aria-expanded={open}
            aria-label={open ? 'Close' : 'Companions'}
            title={open ? 'Close' : 'Companions'}
            onClick={() => setOpen((v) => !v)}
            style={{ background: '#3c5d88', boxShadow: DIAL_SHADOW, transition: 'opacity .2s' }}
            className={
              'flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 text-white hover:opacity-100 ' +
              (open ? 'opacity-100' : 'opacity-[.55]')
            }
          >
            {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </button>
          {open && (companions ?? []).map((c) => <DialPill key={c.key} companion={c} onPick={pick} />)}
        </div>
      )}
      {/* Known to offer nothing: said so, so a check tells "no dial by design" from "not drawn yet" (Board #2717). */}
      {!hidden && companions !== null && companions.length === 0 && (
        <span hidden data-testid="companion-dial-none" />
      )}
      {showing && (
        <CompanionWindow host={host} companion={showing} slug={slug} onClose={() => setShowing(null)} />
      )}
    </>
  );
}
